import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import holidayService from '../../../services/holidayService';
import { formatEasternDateOnly } from '../../../utils/timezone';

const PublicHolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());

  // Form state
  const [formData, setFormData] = useState({
    holidayName: '',
    holidayDate: '',
    holidayType: 'national',
    description: '',
    isRecurring: false
  });

  useEffect(() => {
    loadHolidays();
  }, [selectedYear]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await holidayService?.getHolidaysByYear(selectedYear);
      setHolidays(data || []);
    } catch (err) {
      console.error('Error loading holidays:', err);
      setError(err?.message || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      holidayName: '',
      holidayDate: '',
      holidayType: 'national',
      description: '',
      isRecurring: false
    });
    setEditingHoliday(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      if (!formData?.holidayName || !formData?.holidayDate) {
        setError('Holiday name and date are required');
        return;
      }

      if (editingHoliday) {
        await holidayService?.updateHoliday(editingHoliday?.id, formData);
        setSuccessMessage('Holiday updated successfully');
      } else {
        await holidayService?.createHoliday(formData);
        setSuccessMessage('Holiday added successfully. Attendance records updated automatically.');
      }

      resetForm();
      await loadHolidays();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving holiday:', err);
      setError(err?.message || 'Failed to save holiday');
    }
  };

  const handleEdit = (holiday) => {
    setFormData({
      holidayName: holiday?.holidayName,
      holidayDate: holiday?.holidayDate,
      holidayType: holiday?.holidayType,
      description: holiday?.description || '',
      isRecurring: holiday?.isRecurring
    });
    setEditingHoliday(holiday);
    setShowAddForm(true);
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    try {
      setError('');
      await holidayService?.deleteHoliday(holidayId);
      setSuccessMessage('Holiday deleted successfully');
      await loadHolidays();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting holiday:', err);
      setError(err?.message || 'Failed to delete holiday');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return formatEasternDateOnly(date, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getYearOptions = () => {
    const currentYear = new Date()?.getFullYear();
    const years = [];
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      years?.push({ value: i?.toString(), label: i?.toString() });
    }
    return years;
  };

  const holidayTypeOptions = [
    { value: 'national', label: 'National' },
    { value: 'religious', label: 'Religious' },
    { value: 'regional', label: 'Regional' },
    { value: 'company', label: 'Company Specific' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading holidays...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Public Holiday Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage public holidays that automatically mark attendance as WO (Weekly Off)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedYear?.toString()}
            onChange={(e) => setSelectedYear(parseInt(e?.target?.value))}
            className="w-32 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
          >
            {getYearOptions()?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
          {!showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Add Holiday
            </Button>
          )}
        </div>
      </div>
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <p className="text-sm text-success font-medium">{successMessage}</p>
          </div>
        </div>
      )}
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm text-error font-medium">{error}</p>
          </div>
        </div>
      )}
      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
            </h3>
            <button
              onClick={resetForm}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Holiday Name <span className="text-error">*</span>
                </label>
                <Input
                  value={formData?.holidayName}
                  onChange={(e) => handleInputChange('holidayName', e?.target?.value)}
                  placeholder="e.g., New Year's Day"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Date <span className="text-error">*</span>
                </label>
                <Input
                  type="date"
                  value={formData?.holidayDate}
                  onChange={(e) => handleInputChange('holidayDate', e?.target?.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Holiday Type
                </label>
                <select
                  value={formData?.holidayType}
                  onChange={(e) => handleInputChange('holidayType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                >
                  {holidayTypeOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData?.isRecurring}
                  onChange={(e) => handleInputChange('isRecurring', e?.target?.checked)}
                  className="h-4 w-4 text-primary border-border rounded focus:ring-primary"
                />
                <label htmlFor="isRecurring" className="ml-2 text-sm text-foreground">
                  Recurring annually
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description (Optional)
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Brief description of the holiday"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Holidays List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Holiday Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recurring
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {holidays?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    <Icon name="Calendar" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No holidays found for {selectedYear}</p>
                    <p className="text-sm mt-1">Click "Add Holiday" to create one</p>
                  </td>
                </tr>
              ) : (
                holidays?.map((holiday) => (
                  <tr key={holiday?.id} className="hover:bg-muted/30 transition-smooth">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon name="Calendar" size={16} className="mr-2 text-primary" />
                        <span className="text-sm font-medium text-foreground">
                          {holiday?.holidayName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {formatDate(holiday?.holidayDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                        {holiday?.holidayType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {holiday?.isRecurring ? (
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      ) : (
                        <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate">
                      {holiday?.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(holiday)}
                          className="text-primary hover:text-primary/80 transition-smooth"
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(holiday?.id)}
                          className="text-error hover:text-error/80 transition-smooth"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Info Box */}
      <div className="bg-info/10 border border-info/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} color="var(--color-info)" className="mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-info">How it works</p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>When you add a public holiday, all attendance records for that date are automatically marked as WO (Weekly Off)</li>
              <li>Recurring holidays will repeat annually on the same date</li>
              <li>All employees can view holidays, but only Admin/HR can manage them</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicHolidayManagement;